import { Button, Switch, Tooltip, Select, Table, message, Pagination } from 'antd';
import dayjs from '@/utils/dayjsConfig';
import { ProColumns } from '@ant-design/pro-components';
import type { ProFormInstance } from '@ant-design/pro-components';
import { SettingOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useState, useTransition, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FilterParams, TableHeaderCol } from '@/facilities/model/user';
import { reportMenus } from '@/router/menu';
import { useLocation } from 'react-router-dom';
import { useMenuItem, useUtilsMemo } from '@/hooks';
import { FilterComponet, CustomModal, ExportFile } from '@/components';
import { DATEFILDS, TABLEPAI, NA, MONTH_PARMS } from '@/enums/pageEnum';
import {
  CollapseFilters,
  PrintButton,
  ReportTitle,
  WindowPrint,
  PrintContent,
} from '../index';
import {
  updateTableHeaderConfig,
  getTableHeaderConfig,
  updateReportSort,
  getList,
  getProperOrg,
} from '@api';
import {
  parseQueryString,
  getTimeToString,
  formaReportTable,
  parseMonth,
} from '@/utils/toolt';

export type Key = React.Key;
export default function MPTRCOM({
  tableHeader,
  tableUrl,
  reportSort,
  reportFilter,
  exportUrl,
}: {
  tableHeader: string;
  tableUrl: string;
  reportSort: string;
  reportFilter: string;
  exportUrl: string;
}) {
  const { t } = useTranslation();
  const [lastKeys, setLastKeys] = useState<React.Key[]>([]);
  const [columnsKeys, setColumnsKeys] = useState<React.Key[]>([]);
  const [isPending, startTransition] = useTransition();
  const [data, setData] = useState<object[]>([]);
  const [printData, setPrintData] = useState<object[]>([]);
  const [tableHeaderCols, setTableHeaderCols] = useState<TableHeaderCol[]>([]);
  const [filterDas, setFilterDas] = useState<any>({});
  const formRef = useRef<ProFormInstance>(null);
  const PAGES = { current: 1, size: 5, total: 0 };
  const divClass = 'border-b-[1px] border-[#E8E9ED] dark:border-[#ffffff21] mb-4';
  const [pageParams, setPageParams] = useState({ ...PAGES });
  const defaultSort = useMemo(
    () => ({
      isGroupLift: false,
      sortField: 'alarmSetTs',
      sort: 'DESC',
    }),
    [],
  );
  const [sortbyForm, setSortbyForm] = useState<any>({});
  const [isGroupLift, setIsGroupLift] = useState<boolean>(false);
  const { previousMonth, monthObj } = useUtilsMemo();
  const location = useLocation();
  const [isPrintData, setIsPrintData] = useState(true);

  const getPathParams = useCallback(() => {
    return parseMonth(parseQueryString());
  }, []);

  const getSortParams = useMemo(() => {
    const sortPrsm: any = {};
    if (
      Object.keys(sortbyForm).length &&
      !sortbyForm.isGroupLift &&
      sortbyForm.sortField
    ) {
      sortPrsm.sort = `${sortbyForm.sortField},${sortbyForm.sort}`;
    }
    return sortPrsm;
  }, [sortbyForm]);

  const fetchPrintData = useCallback(
    async (params?: any, groupByLiftName?: boolean) => {
      setIsPrintData(true);
      let page = 1;
      const pageSize = 1000;
      let allData: object[] = [];
      const param = getPathParams();
      const parmsData = { ...getSortParams, ...param, ...params };
      if (!parmsData.month) {
        parmsData.month = dayjs(previousMonth).format(MONTH_PARMS);
      }
      const urlParms = new URLSearchParams(parmsData);
      if (groupByLiftName) {
        urlParms.append('sort', 'liftName,desc');
        urlParms.append('sort', 'alarmSetTs,desc');
        urlParms.append('sort', 'id,desc');
      }
      try {
        while (true) {
          urlParms.append('page', page.toString());
          urlParms.append('size', pageSize.toString());
          const printRes = await getList({
            url: tableUrl,
            params: urlParms,
          });
          const { totalElements, content } = printRes.data.body;
          allData = [...allData, ...content];
          if (allData.length >= totalElements) {
            break;
          }
          page += 1;
        }
        setPrintData(allData);
        setIsPrintData(false);
      } catch (error) {
        message.error(JSON.stringify(error));
        setIsPrintData(false);
      }
    },
    [tableUrl, getSortParams, getPathParams, previousMonth],
  );

  const fetchProperties = useCallback(
    async (date?: Date) => {
      const parms = getPathParams();
      const res = await getProperOrg({
        type: reportFilter,
        viceKey: date ? dayjs(date).format('YYYYMM') : parms.month?.replace('-', ''),
      });
      setFilterDas(res.data.body ? res.data.body.body : []);
    },
    [getPathParams, reportFilter],
  );

  const getData = useCallback(
    (params?: any, groupByLiftName?: boolean) => {
      startTransition(async () => {
        const param = getPathParams();
        try {
          const parmsData = {
            ...getSortParams,
            ...param,
            page: pageParams.current,
            size: pageParams.size,
            ...params,
          };
          if (!parmsData.month) {
            parmsData.month = dayjs(previousMonth).format(MONTH_PARMS);
          }
          const urlParms = new URLSearchParams(parmsData);
          if (groupByLiftName) {
            urlParms.append('sort', 'liftName,desc');
            urlParms.append('sort', 'alarmSetTs,desc');
            urlParms.append('sort', 'id,desc');
          }
          const res = await getList({
            url: tableUrl,
            params: groupByLiftName ? urlParms : parmsData,
          });
          const body = res.data.body;
          setPageParams((prev) => {
            prev.total = body.totalElements;
            return prev;
          });
          setData(body.content);
        } catch (error) {
          message.error(JSON.stringify(error));
        }
      });
    },
    [tableUrl, pageParams, getPathParams, getSortParams, previousMonth],
  );

  const fetchReportSort = useCallback(async () => {
    const res = await getList({
      url: TABLEPAI.USER_PROPERTS,
      params: {
        type: reportSort,
      },
    });
    const body = res.data.body;
    const isShowLift = !!body?.body?.groupBy;
    const sortField = body?.body?.sortField;
    const sort = body?.body?.sort;
    setSortbyForm(
      body ? { isGroupLift: isShowLift, sortField, sort } : { ...defaultSort },
    );
    setIsGroupLift(isShowLift);
    const grLift =
      isShowLift || !sortField ? {} : { sort: `${sortField},${sort || 'DESC'}` };
    const parms = getPathParams();
    if (parms.print && parms.print === 'true') {
      fetchPrintData(grLift, isShowLift);
    } else {
      getData(grLift, isShowLift);
    }
  }, [reportSort, defaultSort, fetchPrintData, getPathParams, getData]);

  const dataSource = useMemo(() => {
    if (sortbyForm.isGroupLift) {
      return formaReportTable(data, 'liftId');
    }
    return data;
  }, [data, sortbyForm.isGroupLift]);

  const getPrintData = useMemo(() => {
    if (sortbyForm.isGroupLift) {
      return formaReportTable(printData, 'liftId');
    }
    return printData;
  }, [printData, sortbyForm.isGroupLift]);

  const isPrintLading = useMemo(() => {
    return getPrintData.length > 0 && !isPrintData && !isPending;
  }, [getPrintData, isPrintData, isPending]);

  const isShowLoading = useMemo(() => {
    const parms = getPathParams();
    return parms.print && parms.print === 'true';
  }, [getPathParams]);

  const getTableLists = useCallback(() => {
    startTransition(async () => {
      try {
        const res = await getTableHeaderConfig(tableHeader);
        await fetchReportSort();
        fetchProperties();
        const cols = res.data.body.body;
        const keys: React.Key[] = [];
        cols.map((item: TableHeaderCol) => {
          if (item.view) {
            keys.push(item.key);
          }
        });
        setColumnsKeys(keys);
        setLastKeys(keys);
        setTableHeaderCols(cols);
      } catch (error) {
        message.error(JSON.stringify(error));
      }
    });
  }, [tableHeader, fetchProperties, fetchReportSort]);

  useMenuItem({
    callBack: (prev?: any) => {
      if (prev && prev?.isReset) {
        setPageParams((prev) => {
          prev.current = 1;
          return prev;
        });
        formRef?.current?.resetFields();
      }
      getTableLists();
    },
  });

  const getTableColumns: ProColumns[] = useMemo(() => {
    const expandableColumns: any[] = [];
    if (!columnsKeys.length) return expandableColumns;
    tableHeaderCols.map(({ key, name }) => {
      if (!columnsKeys.includes(key as Key)) return;
      expandableColumns.push({ title: name, dataIndex: key.split('.') });
    });
    return expandableColumns.map(({ title, dataIndex }, index) => {
      if (index === 0) {
        return {
          title,
          dataIndex,
          onCell: (record) =>
            record.groupLift ? { colSpan: expandableColumns.length } : {},
          render: (value, record) => {
            if (!record.groupLift) return value;
            return (
              <div className="bg-[#F5F6F8] dark:bg-[#ffffff12] expandSubTd border-t-[1px] border-[#D9D9D9] dark:border-[#ffffff21] text-[16px] text-[#AB3E3E] font-semibold p-3">
                <span className="mr-16">{record.liftName}</span>
                <span className="mr-16">Lift age: {record.liftAgeFormat || NA}</span>
                <span className="mr-16">{record.liftCompany}</span>
                <span className="mr-16">
                  {record.lmdAddress || record.buildingAddress}
                </span>
              </div>
            );
          },
        };
      }
      return {
        title,
        dataIndex,
        render: (text: number) => (
          <>{DATEFILDS.includes(dataIndex.toString()) ? getTimeToString(text) : text}</>
        ),
        onCell: (record) => (record.groupLift ? { colSpan: 0 } : {}),
      };
    });
  }, [columnsKeys, tableHeaderCols]);

  const tableTitle = useMemo(() => {
    const parms = getPathParams();
    const month = dayjs(parms.month || previousMonth).format('MMM YYYY');
    const name = `${month} ${t(
      reportMenus.find((item) => item.path === location.pathname)?.name || 'Report',
    )} ${t(' Report')}`;
    return name.replace('Monthly', '');
  }, [location, t, getPathParams, previousMonth]);

  const filtersFileds: FilterParams[] = useMemo(() => {
    return [
      monthObj((date: Date) => {
        formRef?.current?.resetFields();
        fetchProperties(date);
        formRef?.current?.setFieldValue('month', date);
      }),
      {
        component: 'cSel',
        field: 'liftCompanies',
        label: t('LIFT_COMPANY'),
        dataSource: filterDas?.liftCompany,
        fieldProps: { divClass },
      },
      {
        component: 'cSel',
        field: 'contractTerms',
        label: t('contractTerms'),
        dataSource: filterDas?.contractTerms,
        fieldProps: { divClass },
      },
      {
        component: 'cSel',
        field: 'liftBrands',
        label: t('liftBrand'),
        dataSource: filterDas?.liftBrand,
        fieldProps: { divClass },
      },
      {
        component: 'cSel',
        field: 'liftPostalCodes',
        label: t('postalCode'),
        dataSource: filterDas?.postalCode,
        fieldProps: { divClass },
      },
    ];
  }, [t, filterDas, divClass, monthObj, fetchProperties]);

  const getPrintContent = (d: object[]) => {
    return (
      <PrintContent
        columns={getTableColumns}
        loading={isShowLoading ? false : isPending}
        tableTitle={tableTitle}
        dataSource={d}
        className="tableTrapReport"
      />
    );
  };

  const resetFilters = useCallback(() => {
    setPageParams((prev) => {
      prev.current = 1;
      return prev;
    });
    getTableLists();
  }, [getTableLists]);

  if (isShowLoading) {
    return (
      <WindowPrint isPrintLading={isPrintLading}>
        {getPrintContent(getPrintData)}
      </WindowPrint>
    );
  }

  return (
    <div className="p-4">
      <ReportTitle
        title={
          t(reportMenus.find((item) => item.path === location.pathname)?.name || '') +
          t(' Report')
        }
      />
      <CollapseFilters>
        <FilterComponet
          className="mb-[2px] filtersFromClass !py-0 !pl-0"
          layout="horizontal"
          submitNode={<>{t('preview')}</>}
          fieldsProp={filtersFileds}
          ref={formRef}
          submit={() => resetFilters()}
          reset={() => resetFilters()}
        />
      </CollapseFilters>
      <div className="mt-4 p-4 bg-[#fff] dark:bg-[#141414]">
        <div className="flex justify-between items-center pb-4 border-b-[1px] border-[#E8E9ED] dark:border-[#ffffff21]">
          <div className="flex justify-between items-center">
            <ExportFile
              disabled={!data.length}
              onClick={() => {
                const { origin } = new URL(window.location.href);
                const downurl = new URLSearchParams({ ...getPathParams() });
                window.open(`${origin}${exportUrl}?${downurl?.toString()}`);
              }}
            />
            <PrintButton disabled={!data.length} otherParams={getSortParams} />
          </div>
          <div>
            <CustomModal
              title={t('customize')}
              isProForm={false}
              okText={t('confirm')}
              modalName={t('hint.cusReport')}
              className="cusReportModal"
              buttonCogfig={{ variant: 'outlined', icon: <SettingOutlined /> }}
              onClick={(_, callBack) => {
                startTransition(async () => {
                  try {
                    await updateTableHeaderConfig({
                      type: tableHeader,
                      body: columnsKeys,
                    });
                    const { isGroupLift, sortField, sort } = sortbyForm;
                    await updateReportSort({
                      type: reportSort,
                      body: isGroupLift
                        ? { groupBy: 'lift' }
                        : {
                            groupBy: null,
                            sortField: sortField,
                            sort: sortField ? sort || 'DESC' : null,
                          },
                    });
                    message.success(t('success'));
                    setIsGroupLift(isGroupLift);
                    setLastKeys([...columnsKeys]);
                    callBack?.(false);
                    getTableLists();
                  } catch (error) {
                    message.error(JSON.stringify(error));
                    callBack?.(true);
                  }
                });
              }}
              onColseModal={() => {
                setSortbyForm((prv: any) => ({ ...prv, isGroupLift }));
                setColumnsKeys([...lastKeys]);
              }}
            >
              <div>
                <h4 className="text-[16px] font-light mb-3">{t('colTodis')}</h4>
                <Table
                  rowSelection={{
                    selectedRowKeys: columnsKeys,
                    onChange: (rowKeys) => {
                      setColumnsKeys(rowKeys);
                    },
                    getCheckboxProps: (record: any) => ({
                      disabled: !record.detectable,
                    }),
                  }}
                  columns={[{ title: '', dataIndex: 'name' }]}
                  showHeader={false}
                  scroll={{ y: 460 }}
                  pagination={false}
                  rowKey="key"
                  rowClassName="setHostiyRow"
                  dataSource={tableHeaderCols}
                />
              </div>
              <div className="mt-3">
                <h4 className="text-[16px] font-light mb-3">{t('groupSort')}</h4>
                <div className="bg-[#F4F4F4] dark:bg-[#141414] mb-3 px-5 py-3">
                  <div className="flex justify-between items-center">
                    <div className="flex justify-between items-center">
                      <div className="text-[14px]">{t('groupLift')}</div>
                      <div>
                        <Tooltip title={<p>{t('hint.toggleSwitch')}</p>}>
                          <Button
                            color="default"
                            icon={
                              <InfoCircleOutlined
                                className="text-[#000000d9] dark:text-[#ffffffd9]"
                                style={{ fontSize: 16 }}
                              />
                            }
                            variant="link"
                          />
                        </Tooltip>
                      </div>
                    </div>
                    <div>
                      <Switch
                        value={sortbyForm.isGroupLift}
                        onChange={() => {
                          setSortbyForm((prv: any) => ({
                            ...defaultSort,
                            isGroupLift: !prv.isGroupLift,
                          }));
                        }}
                      />
                    </div>
                  </div>
                  {!sortbyForm.isGroupLift && (
                    <div className="flex justify-between items-center mt-4">
                      <div>{t('sortBy')}: </div>
                      <div>
                        <Select
                          style={{ width: 380 }}
                          onChange={(e) =>
                            setSortbyForm((prv: any) => ({
                              ...prv,
                              sortField: e,
                            }))
                          }
                          value={sortbyForm.sortField}
                          fieldNames={{ label: 'name', value: 'sortField' }}
                          placeholder={t('Please select')}
                          options={tableHeaderCols.filter(
                            (item: any) => item.sort && item,
                          )}
                        />
                      </div>
                      <div>
                        <Select
                          style={{ width: 150 }}
                          value={sortbyForm.sort}
                          onChange={(e) => {
                            setSortbyForm((prv: any) => ({ ...prv, sort: e }));
                          }}
                          placeholder={t('descending')}
                          options={[
                            { value: 'ASC', label: t('ascending') },
                            { value: 'DESC', label: t('descending') },
                          ]}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </CustomModal>
          </div>
        </div>
        {getPrintContent(dataSource)}
        <div className="pt-4">
          <Pagination
            align="end"
            current={pageParams.current}
            onChange={(page) => {
              setPageParams((prev) => {
                prev.current = page;
                return prev;
              });
              getData({ page, ...getSortParams }, isGroupLift);
            }}
            pageSize={pageParams.size}
            showSizeChanger={false}
            total={pageParams.total}
          />
        </div>
      </div>
    </div>
  );
}
